/**
 * Chroma-key helpers (Agent Air, masking rework #3) — pure functions that turn a
 * key color + tolerance into a print-area polygon from the generated image's
 * GREEN patch. No DOM here: callers feed in a flat RGBA pixel buffer (the
 * MaskEditor reads it from an offscreen <canvas>; tests feed a synthetic grid).
 *
 * Pipeline: per-pixel Euclidean RGB distance → boolean match grid → largest
 * connected matching region (4-connectivity flood fill) → marching-squares
 * contour traced into a normalized 0..1 polygon, simplified (Douglas–Peucker via
 * the engine's `geometry.simplify`). Falls back to the bounding box of matching
 * pixels when no clean contour is found. Coordinates are normalized 0..1.
 */
import type { Polygon } from "@genz/engine-bind";
import { geometry } from "@genz/engine-bind";

/** An RGB color as 0..255 channels. */
export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** The maximum possible Euclidean RGB distance (√(255² · 3) ≈ 441.67). */
export const MAX_RGB_DISTANCE = Math.sqrt(255 * 255 * 3);

/** Parse a `#rrggbb` (or `#rgb`) hex string to RGB; returns null if malformed. */
export function parseHexColor(hex: string): Rgb | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

/** Format an RGB as a lowercase `#rrggbb` hex string. */
export function toHexColor({ r, g, b }: Rgb): string {
  const h = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}

/** Euclidean distance in RGB space between two colors (0..441.67). */
export function colorDistance(a: Rgb, b: Rgb): number {
  const dr = a.r - b.r;
  const dg = a.g - b.g;
  const db = a.b - b.b;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

/**
 * Sample the RGB color of one pixel from an RGBA buffer at integer pixel
 * coordinates. Coordinates are clamped into range, so an eyedropper click that
 * lands on (or just past) the edge still returns a valid color. Returns null
 * only for an empty/degenerate buffer. The MaskEditor uses this to turn an
 * eyedropper click on the displayed image into the key color (+ hex code).
 */
export function sampleColorAt(
  rgba: Uint8ClampedArray | Uint8Array | number[],
  width: number,
  height: number,
  x: number,
  y: number,
): Rgb | null {
  if (width <= 0 || height <= 0) return null;
  const px = clamp(Math.floor(x), 0, width - 1);
  const py = clamp(Math.floor(y), 0, height - 1);
  const o = (py * width + px) * 4;
  if (o + 2 >= rgba.length) return null;
  return { r: rgba[o], g: rgba[o + 1], b: rgba[o + 2] };
}

/**
 * Produce a NEW RGBA buffer with every pixel within `tolerancePct` of `key` made
 * fully transparent (alpha 0); all other pixels (including their alpha) are
 * copied unchanged. This is the chroma "removal" the product owner asked for —
 * the keyed color becomes transparent rather than being recolored. Pure: no DOM,
 * deterministic, and the result is what `detectTransparentRegion` (placement.ts)
 * then auto-detects. The input buffer is not mutated.
 */
export function applyChromaKey(
  rgba: Uint8ClampedArray | Uint8Array | number[],
  width: number,
  height: number,
  key: Rgb,
  tolerancePct: number,
): Uint8ClampedArray {
  const threshold = (clamp(tolerancePct, 0, 100) / 100) * MAX_RGB_DISTANCE;
  const out = new Uint8ClampedArray(width * height * 4);
  for (let i = 0; i < width * height; i++) {
    const o = i * 4;
    out[o] = rgba[o];
    out[o + 1] = rgba[o + 1];
    out[o + 2] = rgba[o + 2];
    // Already-transparent stays transparent; opaque pixels matching the key drop to 0.
    const a = rgba[o + 3];
    if (a === 0) {
      out[o + 3] = 0;
      continue;
    }
    const dr = rgba[o] - key.r;
    const dg = rgba[o + 1] - key.g;
    const db = rgba[o + 2] - key.b;
    out[o + 3] = Math.sqrt(dr * dr + dg * dg + db * db) <= threshold ? 0 : a;
  }
  return out;
}

/**
 * Whether a pixel is within `tolerancePct` (0..100) of the key color. The
 * tolerance maps linearly onto 0..MAX_RGB_DISTANCE.
 */
export function pixelMatches(pixel: Rgb, key: Rgb, tolerancePct: number): boolean {
  const threshold = (clamp(tolerancePct, 0, 100) / 100) * MAX_RGB_DISTANCE;
  return colorDistance(pixel, key) <= threshold;
}

/**
 * Compute a boolean match grid (row-major, `width * height`) from an RGBA buffer
 * (4 bytes/pixel, e.g. `ctx.getImageData().data`). Fully transparent pixels
 * (alpha 0) never match.
 */
export function matchGrid(
  rgba: Uint8ClampedArray | Uint8Array | number[],
  width: number,
  height: number,
  key: Rgb,
  tolerancePct: number,
): Uint8Array {
  const threshold = (clamp(tolerancePct, 0, 100) / 100) * MAX_RGB_DISTANCE;
  const grid = new Uint8Array(width * height);
  for (let i = 0; i < width * height; i++) {
    const o = i * 4;
    if (rgba[o + 3] === 0) continue;
    const dr = rgba[o] - key.r;
    const dg = rgba[o + 1] - key.g;
    const db = rgba[o + 2] - key.b;
    if (Math.sqrt(dr * dr + dg * dg + db * db) <= threshold) grid[i] = 1;
  }
  return grid;
}

/** Axis-aligned bounding box in pixel coordinates (inclusive max). */
export interface PixelBBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * The largest 4-connected region of matching pixels: returns the boolean mask of
 * that region (row-major) plus its bounding box, or null if nothing matched.
 */
export function largestRegion(
  grid: Uint8Array,
  width: number,
  height: number,
): { mask: Uint8Array; bbox: PixelBBox; size: number } | null {
  const labels = new Int32Array(width * height).fill(-1);
  const stack: number[] = [];
  let best = { label: -1, size: 0 };
  let label = 0;
  for (let start = 0; start < grid.length; start++) {
    if (grid[start] === 0 || labels[start] !== -1) continue;
    labels[start] = label;
    stack.length = 0;
    stack.push(start);
    let size = 0;
    while (stack.length) {
      const idx = stack.pop()!;
      size++;
      const x = idx % width;
      const y = (idx - x) / width;
      if (x > 0) pushIf(idx - 1);
      if (x < width - 1) pushIf(idx + 1);
      if (y > 0) pushIf(idx - width);
      if (y < height - 1) pushIf(idx + width);
    }
    if (size > best.size) best = { label, size };
    label++;
  }
  function pushIf(n: number) {
    if (grid[n] === 1 && labels[n] === -1) {
      labels[n] = label;
      stack.push(n);
    }
  }
  if (best.label < 0) return null;

  const mask = new Uint8Array(width * height);
  const bbox: PixelBBox = { minX: width, minY: height, maxX: -1, maxY: -1 };
  for (let i = 0; i < labels.length; i++) {
    if (labels[i] !== best.label) continue;
    mask[i] = 1;
    const x = i % width;
    const y = (i - x) / width;
    if (x < bbox.minX) bbox.minX = x;
    if (y < bbox.minY) bbox.minY = y;
    if (x > bbox.maxX) bbox.maxX = x;
    if (y > bbox.maxY) bbox.maxY = y;
  }
  return { mask, bbox, size: best.size };
}

/**
 * Marching-squares boundary trace of a binary region mask. Returns an ordered
 * loop of grid-corner vertices (pixel coordinates, where vertex (gx,gy) sits at
 * the corner between cells), or null if no boundary is found. Uses Moore-style
 * neighbor following on the cell grid; robust to a single blob.
 */
export function traceContour(mask: Uint8Array, width: number, height: number): { x: number; y: number }[] | null {
  // Sample helper: is cell (cx,cy) inside the region? Out-of-range = outside.
  const inside = (cx: number, cy: number): boolean =>
    cx >= 0 && cy >= 0 && cx < width && cy < height && mask[cy * width + cx] === 1;

  // Find a starting boundary corner: scan for the first inside cell; its
  // top-left corner is on the boundary.
  let startX = -1;
  let startY = -1;
  for (let i = 0; i < mask.length && startX < 0; i++) {
    if (mask[i] === 1) {
      startX = i % width;
      startY = (i - (i % width)) / width;
    }
  }
  if (startX < 0) return null;

  // Square-tracing on the corner lattice (corners range 0..width, 0..height).
  // State = current corner + direction we entered from. Use the 4-case
  // marching-squares lookup based on the four cells around a corner.
  const path: { x: number; y: number }[] = [];
  let cx = startX;
  let cy = startY; // corner coordinates
  let dir = 0; // 0=right,1=down,2=left,3=up — initial guess refined below
  const sx = cx;
  const sy = cy;
  let guard = 0;
  const maxSteps = (width + 2) * (height + 2) * 4;
  do {
    path.push({ x: cx, y: cy });
    // The four cells touching corner (cx,cy): TL,TR,BL,BR.
    const tl = inside(cx - 1, cy - 1);
    const tr = inside(cx, cy - 1);
    const bl = inside(cx - 1, cy);
    const br = inside(cx, cy);
    // Marching-squares case → next move direction (clockwise boundary).
    const code = (tl ? 1 : 0) | (tr ? 2 : 0) | (br ? 4 : 0) | (bl ? 8 : 0);
    let move: number;
    switch (code) {
      case 1: move = 3; break; // up
      case 2: move = 0; break; // right
      case 3: move = 0; break;
      case 4: move = 1; break; // down
      case 5: move = dir === 0 ? 1 : 3; break; // saddle — keep turning
      case 6: move = 1; break;
      case 7: move = 1; break;
      case 8: move = 2; break; // left
      case 9: move = 3; break;
      case 10: move = dir === 1 ? 0 : 2; break; // saddle
      case 11: move = 0; break;
      case 12: move = 2; break;
      case 13: move = 3; break;
      case 14: move = 2; break;
      default: return path.length >= 4 ? path : null; // 0/15 — off the boundary
    }
    dir = move;
    if (move === 0) cx++;
    else if (move === 1) cy++;
    else if (move === 2) cx--;
    else cy--;
    guard++;
  } while ((cx !== sx || cy !== sy) && guard < maxSteps);

  return path.length >= 4 ? path : null;
}

/**
 * High-level: from an RGBA buffer + key color + tolerance, produce a normalized
 * 0..1 print-area polygon for the largest matching region. Traces a contour and
 * simplifies it (ε ~0.01 normalized); falls back to the region bounding box when
 * no clean contour is found. Returns null when nothing matches.
 */
export function regionToPolygon(
  rgba: Uint8ClampedArray | Uint8Array | number[],
  width: number,
  height: number,
  key: Rgb,
  tolerancePct: number,
  epsilon = 0.01,
): Polygon | null {
  if (width <= 0 || height <= 0) return null;
  const grid = matchGrid(rgba, width, height, key, tolerancePct);
  const region = largestRegion(grid, width, height);
  if (!region) return null;

  const norm = (px: number, py: number) => ({ x: clamp(px / width, 0, 1), y: clamp(py / height, 0, 1) });
  const contour = traceContour(region.mask, width, height);
  if (contour && contour.length >= 4) {
    const poly: Polygon = contour.map((p) => norm(p.x, p.y));
    const simplified = geometry.simplify(poly, epsilon);
    if (simplified.length >= 3) return simplified;
  }
  // Fallback: bounding box of the matching region (maxX/maxY are inclusive, so
  // +1 to reach the far edge of those pixels).
  const { minX, minY, maxX, maxY } = region.bbox;
  return [
    norm(minX, minY),
    norm(maxX + 1, minY),
    norm(maxX + 1, maxY + 1),
    norm(minX, maxY + 1),
  ];
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

// ============================================================================
// Phase-1 flagship background removal: YCbCr keying + soft alpha + spill
// suppression + flood-fill ("magic wand") + multi-sample keys. These are the
// quality upgrade over the flat RGB binary key above (which the legacy seed path
// still uses). Keying in the Cb/Cr CHROMA plane ignores luminance, so shadows and
// uneven lighting on the backdrop no longer break the key.
// ============================================================================

/**
 * RGB (0..255) → HSV with `h` in [0,360), `s`,`v` in [0,1]. Pure + deterministic.
 * Used by the saturation/value GATE in applyChromaKeyAdvanced: vivid coloured print
 * areas (high S) are separated from warm-but-muted backdrops (low S) on exactly the
 * axis Cb/Cr discards — so warm wood never leaks into a vivid-orange key.
 */
export function rgbToHsv({ r, g, b }: Rgb): { h: number; s: number; v: number } {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const mx = Math.max(rn, gn, bn);
  const mn = Math.min(rn, gn, bn);
  const d = mx - mn;
  const v = mx;
  const s = mx <= 1e-9 ? 0 : d / mx;
  let h = 0;
  if (d > 1e-9) {
    if (mx === rn) h = ((gn - bn) / d) % 6;
    else if (mx === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s, v };
}

/** RGB → Cb/Cr (the chroma plane of YCbCr; luma Y is intentionally dropped). */
export function rgbToCbCr({ r, g, b }: Rgb): { cb: number; cr: number } {
  return {
    cb: 128 - 0.168736 * r - 0.331264 * g + 0.5 * b,
    cr: 128 + 0.5 * r - 0.418688 * g - 0.081312 * b,
  };
}

/** Max possible Cb/Cr distance (≈ 180.3) — used to map a 0..100% tolerance. */
export const MAX_CBCR_DISTANCE = Math.sqrt(2) * 127.5;

/** Chroma-plane distance between two colors (ignores brightness). */
export function chromaDistance(pixel: Rgb, key: Rgb): number {
  const a = rgbToCbCr(pixel);
  const b = rgbToCbCr(key);
  const dcb = a.cb - b.cb;
  const dcr = a.cr - b.cr;
  return Math.sqrt(dcb * dcb + dcr * dcr);
}

/** Min chroma distance from a pixel to the NEAREST of several sampled keys. */
function nearestChroma(pixel: Rgb, keys: Rgb[]): number {
  let best = Infinity;
  for (const k of keys) {
    const d = chromaDistance(pixel, k);
    if (d < best) best = d;
  }
  return best;
}

/** Index of the NEAREST key (by chroma distance), or -1 for an empty key set. */
function nearestChromaIdx(pixel: Rgb, keys: Rgb[]): number {
  let best = Infinity;
  let idx = -1;
  for (let i = 0; i < keys.length; i++) {
    const d = chromaDistance(pixel, keys[i]);
    if (d < best) { best = d; idx = i; }
  }
  return idx;
}

/**
 * Saturation/value gate (spec 022): is `pixel` vivid/bright enough RELATIVE to the
 * nearest key to be a genuine key match? Vivid keys (e.g. orange S≈0.92) reject muted
 * warm backdrops (wood S≈0.6) on the axis Cb/Cr discards. Auto-passes when the nearest
 * key is near-neutral (key.s < 0.25) so flat grey/white backdrops still key.
 */
function passesSatGate(pixel: Rgb, keyHsv: { h: number; s: number; v: number }, satFrac: number, valFrac: number): boolean {
  if (keyHsv.s < 0.25) return true; // neutral key → gate disabled (flat-backdrop case)
  const p = rgbToHsv(pixel);
  return p.s >= satFrac * keyHsv.s && p.v >= valFrac * keyHsv.v;
}

export interface ChromaRemovalOptions {
  /** ≤ inner% chroma distance → fully transparent. Default ~ tolerance×0.6. */
  innerPct?: number;
  /** ≥ outer% → fully opaque; between inner & outer → soft alpha ramp (feather). */
  outerPct?: number;
  /** Desaturate the key-color cast on partially-removed edge pixels (de-fringe). */
  spill?: boolean;
  /** Magic-wand seed: only remove the matching region CONNECTED to this pixel. */
  seed?: { x: number; y: number };
  /**
   * Saturation/value GATE (relative to the nearest sampled key, spec 022). A pixel is
   * only keyable when its HSV `s >= satFrac*key.s` AND `v >= valFrac*key.v` — so a vivid
   * key (e.g. orange S≈0.92) cannot remove a muted warm backdrop (wood S≈0.6) that Cb/Cr
   * alone treats as close. ANDed onto the chroma test → can only ADD rejection. Auto-OFF
   * when the nearest key is itself near-neutral (key.s < 0.25), so flat grey/white
   * backdrops still key. Default on.
   */
  satGate?: boolean;
  /** Min fraction of the key's saturation a pixel must have to be keyed. Default 0.72. */
  satFrac?: number;
  /** Min fraction of the key's value a pixel must have to be keyed. Default 0.25. */
  valFrac?: number;
}

/**
 * Flagship chroma removal → a NEW RGBA buffer. Distance is measured in the Cb/Cr
 * chroma plane against the nearest of `keys`; alpha ramps softly between the inner
 * and outer thresholds (anti-aliased edges, not jaggies); optional spill
 * suppression de-fringes edges; an optional `seed` restricts removal to the
 * contiguous background region (magic wand) so lookalike pixels inside the product
 * are kept. Input is not mutated.
 */
export function applyChromaKeyAdvanced(
  rgba: Uint8ClampedArray | Uint8Array | number[],
  width: number,
  height: number,
  keys: Rgb[],
  tolerancePct: number,
  opts: ChromaRemovalOptions = {},
): Uint8ClampedArray {
  const tol = clamp(tolerancePct, 0, 100);
  const inner = ((opts.innerPct ?? tol * 0.6) / 100) * MAX_CBCR_DISTANCE;
  const outer = Math.max(inner + 1, ((opts.outerPct ?? tol) / 100) * MAX_CBCR_DISTANCE);
  const spill = opts.spill ?? true;
  const satGate = opts.satGate ?? true;
  const satFrac = opts.satFrac ?? 0.72;
  const valFrac = opts.valFrac ?? 0.25;
  const keyHsv = keys.map(rgbToHsv);
  const n = width * height;
  // Start as a full copy of the input (RGB kept; alpha + spill adjusted below).
  const out = Uint8ClampedArray.from(rgba as ArrayLike<number>);

  /** True when `px` matches the key by chroma distance AND (if enabled) the sat/value
   *  gate against the SAME nearest key — the gate severs a vivid key from a muted
   *  lookalike backdrop so the flood cannot bridge orange → wood. */
  const matches = (px: Rgb): boolean => {
    const idx = nearestChromaIdx(px, keys);
    if (idx < 0 || chromaDistance(px, keys[idx]) > outer) return false;
    return !satGate || passesSatGate(px, keyHsv[idx], satFrac, valFrac);
  };

  // Optional flood-fill: which pixels are eligible to be removed (connected to seed).
  let eligible: Uint8Array | null = null;
  if (opts.seed) {
    const grid = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
      const o = i * 4;
      if (rgba[o + 3] === 0) { grid[i] = 1; continue; } // transparent counts as bg
      if (matches({ r: rgba[o], g: rgba[o + 1], b: rgba[o + 2] })) grid[i] = 1;
    }
    eligible = floodFrom(grid, width, height,
      clamp(Math.floor(opts.seed.x), 0, width - 1),
      clamp(Math.floor(opts.seed.y), 0, height - 1));
  }

  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const a0 = rgba[o + 3];
    if (a0 === 0) { out[o + 3] = 0; continue; }
    if (eligible && eligible[i] === 0) continue; // outside the wand region → untouched
    const px = { r: rgba[o], g: rgba[o + 1], b: rgba[o + 2] };
    const idx = nearestChromaIdx(px, keys);
    const d = idx < 0 ? Infinity : chromaDistance(px, keys[idx]);
    // The sat/value gate forces a pixel to be fully KEPT regardless of chroma distance,
    // so even the no-seed standalone key never eats a muted warm backdrop.
    const gateOk = idx >= 0 && (!satGate || passesSatGate(px, keyHsv[idx], satFrac, valFrac));
    // keepFactor: 0 = fully removed, 1 = fully kept.
    let keep: number;
    if (!gateOk) keep = 1;
    else if (d <= inner) keep = 0;
    else if (d >= outer) keep = 1;
    else keep = (d - inner) / (outer - inner);
    out[o + 3] = Math.round(a0 * keep);
    if (spill && keep > 0 && keep < 1) {
      // De-fringe: pull the partially-keyed pixel toward its own grey (removes the
      // colour cast bleeding onto product edges). GATED to genuine mid fringe (S≥0.5,
      // V≤0.85) so bright in-product specular reflections (e.g. orange-tinted camera
      // glass) are NOT greyed — the "removing part of the camera" fix (spec 022).
      const hsv = rgbToHsv(px);
      if (hsv.s >= 0.5 && hsv.v <= 0.85) {
        const grey = 0.299 * px.r + 0.587 * px.g + 0.114 * px.b;
        const m = (1 - keep) * 0.5;
        out[o] = Math.round(px.r + (grey - px.r) * m);
        out[o + 1] = Math.round(px.g + (grey - px.g) * m);
        out[o + 2] = Math.round(px.b + (grey - px.b) * m);
      }
    }
  }
  return out;
}

/**
 * Sample representative BACKGROUND colors from the image border (4 corners + 4 edge
 * midpoints), de-duplicated in the chroma plane. Used by automatic background
 * removal: the product is assumed centered, so the border is background.
 */
export function sampleBorderKeys(
  rgba: Uint8ClampedArray | Uint8Array | number[],
  width: number,
  height: number,
): Rgb[] {
  if (width <= 0 || height <= 0) return [];
  const at = (x: number, y: number): Rgb => {
    const o = (clamp(y, 0, height - 1) * width + clamp(x, 0, width - 1)) * 4;
    return { r: rgba[o], g: rgba[o + 1], b: rgba[o + 2] };
  };
  const cand = [
    at(0, 0), at(width - 1, 0), at(0, height - 1), at(width - 1, height - 1),
    at(width >> 1, 0), at(width >> 1, height - 1), at(0, height >> 1), at(width - 1, height >> 1),
  ];
  const keys: Rgb[] = [];
  for (const c of cand) {
    if (!keys.some((k) => chromaDistance(k, c) < 12)) keys.push(c);
  }
  return keys;
}

/**
 * One-click AUTOMATIC background removal (no model): key off the border colors and
 * flood-fill inward from EVERY border pixel, removing only the connected background
 * with soft feathered alpha + spill suppression. Lookalike colors inside the product
 * are kept (they aren't connected to the border). Returns a NEW RGBA buffer.
 *
 * This is the classical-CV flagship. The same call site can later swap in an
 * on-device WASM segmentation model via the SegmentationProvider seam (see below)
 * for photos where the background isn't a flat border.
 */
export function applyAutoBackgroundRemoval(
  rgba: Uint8ClampedArray | Uint8Array | number[],
  width: number,
  height: number,
  tolerancePct = 22,
  opts: { spill?: boolean } = {},
): Uint8ClampedArray {
  const n = width * height;
  const out = Uint8ClampedArray.from(rgba as ArrayLike<number>);
  if (n === 0) return out;
  const keys = sampleBorderKeys(rgba, width, height);
  if (keys.length === 0) return out;
  const tol = clamp(tolerancePct, 0, 100);
  const inner = (tol * 0.6 / 100) * MAX_CBCR_DISTANCE;
  const outer = Math.max(inner + 1, (tol / 100) * MAX_CBCR_DISTANCE);
  const spill = opts.spill ?? true;

  // Eligible = background-colored (or already transparent).
  const grid = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    if (rgba[o + 3] === 0) { grid[i] = 1; continue; }
    if (nearestChroma({ r: rgba[o], g: rgba[o + 1], b: rgba[o + 2] }, keys) <= outer) grid[i] = 1;
  }
  // Multi-seed flood from all border cells that are eligible.
  const region = new Uint8Array(n);
  const stack: number[] = [];
  const seed = (idx: number) => { if (grid[idx] === 1 && region[idx] === 0) { region[idx] = 1; stack.push(idx); } };
  for (let x = 0; x < width; x++) { seed(x); seed((height - 1) * width + x); }
  for (let y = 0; y < height; y++) { seed(y * width); seed(y * width + width - 1); }
  while (stack.length) {
    const idx = stack.pop()!;
    const x = idx % width;
    const y = (idx - x) / width;
    if (x > 0) seed(idx - 1);
    if (x < width - 1) seed(idx + 1);
    if (y > 0) seed(idx - width);
    if (y < height - 1) seed(idx + width);
  }
  // Apply soft removal only inside the connected background region.
  for (let i = 0; i < n; i++) {
    if (region[i] === 0) continue;
    const o = i * 4;
    const a0 = rgba[o + 3];
    if (a0 === 0) { out[o + 3] = 0; continue; }
    const px = { r: rgba[o], g: rgba[o + 1], b: rgba[o + 2] };
    const d = nearestChroma(px, keys);
    let keep: number;
    if (d <= inner) keep = 0;
    else if (d >= outer) keep = 1;
    else keep = (d - inner) / (outer - inner);
    out[o + 3] = Math.round(a0 * keep);
    if (spill && keep > 0 && keep < 1 && (() => { const hsv = rgbToHsv(px); return hsv.s >= 0.5 && hsv.v <= 0.85; })()) {
      // Despill gated to genuine mid fringe (S≥0.5, V≤0.85) so bright in-product
      // specular highlights aren't greyed (spec 022); strength 0.8 → 0.5.
      const grey = 0.299 * px.r + 0.587 * px.g + 0.114 * px.b;
      const m = (1 - keep) * 0.5;
      out[o] = Math.round(px.r + (grey - px.r) * m);
      out[o + 1] = Math.round(px.g + (grey - px.g) * m);
      out[o + 2] = Math.round(px.b + (grey - px.b) * m);
    }
  }
  return out;
}

/**
 * Seam for the FUTURE on-device flagship: a swappable background segmenter. The
 * classical border-flood above is the default `classical` provider. An `onnx`
 * provider (onnxruntime-web + a bundled MODNet/U²-Net) implements the same
 * contract and is selected when the model asset is present — drop-in, no call-site
 * change. (Model artifact + runtime dependency are the remaining work for ML.)
 */
export interface SegmentationProvider {
  readonly id: "classical" | "onnx";
  /** Return a NEW RGBA buffer with the background removed (alpha 0). */
  removeBackground(
    rgba: Uint8ClampedArray, width: number, height: number,
  ): Promise<Uint8ClampedArray> | Uint8ClampedArray;
}

export const classicalSegmenter: SegmentationProvider = {
  id: "classical",
  removeBackground: (rgba, width, height) => applyAutoBackgroundRemoval(rgba, width, height),
};

// ============================================================================
// Green-spill despill + spill-key pass (the clear-case fix). The legacy "spill"
// in applyChromaKeyAdvanced only de-fringes partially-keyed EDGE pixels by greying
// them — so a green CAST inside surviving material (e.g. the clear plastic's
// refraction) is never touched, and where it does fire it muddies the plastic.
// These operate GLOBALLY in RGB instead: the green-channel limiter
// G' = min(G, max(R,B)) neutralizes the cast on every surviving pixel, and the
// spill key removes transparency off the green EXCESS (not hue) — so white glare /
// highlights, which have G ≈ max(R,B) → ~0 spill, are kept automatically.
// ============================================================================

/** Green excess over the brighter of red/blue: max(0, G − max(R,B)). 0 for
 *  neutral, white, and non-green pixels; large for green-tinted / pure-green ones. */
export function greenSpill({ r, g, b }: Rgb): number {
  return Math.max(0, g - Math.max(r, b));
}

/**
 * Neutralize one pixel's green cast in place within `out` at byte offset `o`:
 * pull the green channel down toward max(R,B) by `amount`, optionally rescaling
 * RGB to restore the original luma so the pixel stays bright. `spill` is the
 * pre-computed green excess (skip the write when ≤ 0). Shared by despillGreen and
 * applyGreenSpillKey.
 */
function despillPixel(
  out: Uint8ClampedArray,
  o: number,
  r: number,
  g: number,
  b: number,
  spill: number,
  amount: number,
  preserveLuma: boolean,
): void {
  if (spill <= 0) return;
  const g2 = g - spill * amount;
  if (!preserveLuma) {
    out[o + 1] = Math.round(g2);
    return;
  }
  const l0 = 0.299 * r + 0.587 * g + 0.114 * b;
  const l1 = 0.299 * r + 0.587 * g2 + 0.114 * b;
  const s = l1 > 1e-6 ? l0 / l1 : 1;
  out[o] = Math.round(r * s);
  out[o + 1] = Math.round(g2 * s);
  out[o + 2] = Math.round(b * s);
}

export interface DespillOptions {
  /** 0..1 fraction of the green excess to remove (1 = full clamp to max(R,B)). */
  amount?: number;
  /** Rescale RGB to restore the pixel's original luma so the plastic doesn't darken. */
  preserveLuma?: boolean;
}

/**
 * Global green despill → a NEW RGBA buffer. Neutralizes the green colour cast on
 * EVERY pixel (not just keyed edges) by pulling the green channel down toward
 * max(R,B) by `amount`; R, B, and alpha are untouched (unless `preserveLuma`,
 * which then scales the whole pixel back to its original brightness so reflections
 * / refraction stay bright instead of going muddy). Input is not mutated.
 */
export function despillGreen(
  rgba: Uint8ClampedArray | Uint8Array | number[],
  width: number,
  height: number,
  opts: DespillOptions = {},
): Uint8ClampedArray {
  const amount = clamp(opts.amount ?? 1, 0, 1);
  const preserveLuma = opts.preserveLuma ?? false;
  const out = Uint8ClampedArray.from(rgba as ArrayLike<number>);
  const n = width * height;
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const r = rgba[o], g = rgba[o + 1], b = rgba[o + 2];
    despillPixel(out, o, r, g, b, g - Math.max(r, b), amount, preserveLuma);
  }
  return out;
}

export interface GreenSpillKeyOptions {
  /** Spill (0..255) at/below which a pixel is fully KEPT (opaque). Default 12. */
  innerSpill?: number;
  /** Spill at/above which a pixel is fully REMOVED (alpha 0). Default 90. */
  outerSpill?: number;
  /** Despill surviving pixels (neutralize the green cast). Default true. */
  despill?: boolean;
  /** Despill strength 0..1 (see despillGreen). Default 1. */
  despillAmount?: number;
  /** Restore luma after despill so kept plastic/refraction stays bright. Default true. */
  preserveLuma?: boolean;
}

/**
 * Flagship spill key → a NEW RGBA buffer. Alpha is keyed off the green EXCESS
 * (max(0, G − max(R,B))), NOT hue: pixels above `outerSpill` go transparent, below
 * `innerSpill` stay opaque, and the band between ramps softly (anti-aliased edges).
 * Because white highlights have G ≈ max(R,B) → ~0 spill, glare and reflections are
 * kept automatically. Every surviving pixel is then despilled so the green cast in
 * the clear plastic's refraction is removed too. Input is not mutated.
 */
export function applyGreenSpillKey(
  rgba: Uint8ClampedArray | Uint8Array | number[],
  width: number,
  height: number,
  opts: GreenSpillKeyOptions = {},
): Uint8ClampedArray {
  const inner = Math.max(0, opts.innerSpill ?? 12);
  const outer = Math.max(inner + 1, opts.outerSpill ?? 90);
  const despill = opts.despill ?? true;
  const despillAmount = clamp(opts.despillAmount ?? 1, 0, 1);
  const preserveLuma = opts.preserveLuma ?? true;
  const out = Uint8ClampedArray.from(rgba as ArrayLike<number>);
  const n = width * height;
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const a0 = rgba[o + 3];
    if (a0 === 0) { out[o + 3] = 0; continue; }
    const r = rgba[o], g = rgba[o + 1], b = rgba[o + 2];
    const spill = Math.max(0, g - Math.max(r, b));
    let keep: number;
    if (spill <= inner) keep = 1;
    else if (spill >= outer) keep = 0;
    else keep = (outer - spill) / (outer - inner);
    out[o + 3] = Math.round(a0 * keep);
    if (keep > 0 && despill) despillPixel(out, o, r, g, b, spill, despillAmount, preserveLuma);
  }
  return out;
}

/**
 * EDGE REFINE — choke (erode) or spread (dilate) the alpha matte by `radius` px, then
 * optionally feather. `radius > 0` CHOKES: shrinks the opaque region (eats a thin
 * semi-transparent key fringe at the boundary — the "remove the green/colour halo" fix).
 * `radius < 0` SPREADS: grows the opaque region back (recovers an over-removed edge, e.g.
 * a transparent case the auto-remover ate). `feather` blurs the alpha edge for a soft cut.
 * RGB is preserved; only alpha is reshaped. Pure + deterministic. Input is not mutated.
 */
export function refineMatte(
  rgba: Uint8ClampedArray | Uint8Array | number[],
  width: number,
  height: number,
  opts: { radius?: number; feather?: number } = {},
): Uint8ClampedArray {
  const out = Uint8ClampedArray.from(rgba as ArrayLike<number>);
  const n = width * height;
  const radius = Math.round(opts.radius ?? 0);
  const feather = Math.max(0, Math.round(opts.feather ?? 0));
  // Morphology on the alpha plane: choke = min-filter (erode opaque), spread = max-filter.
  let alpha: Uint8Array = new Uint8Array(n);
  for (let i = 0; i < n; i++) alpha[i] = (rgba[i * 4 + 3] as number) | 0;
  const passes = Math.abs(radius);
  if (passes > 0) alpha = morphSquare(alpha, width, height, passes, radius > 0);
  // Optional box-blur feather of the alpha edge (separable, `feather` px radius).
  if (feather > 0) alpha = featherMask(alpha, width, height, feather);
  for (let i = 0; i < n; i++) out[i * 4 + 3] = alpha[i];
  return out;
}

/**
 * SQUARE MIN/MAX MORPHOLOGY — erode (`erode`) or dilate a single-channel plane by `r` pixels.
 *
 * Exactly equivalent to the naive "apply a 3×3 min/max filter `r` times" this replaced: a square
 * structuring element is additive under repeated application (r successive 3×3 squares = one
 * (2r+1) square), and on a RECTANGULAR domain every in-bounds pixel within Chebyshev distance r is
 * reachable by unit steps that stay in bounds, so the boundary-clamping behaviour matches too.
 *
 * Why it was rewritten (audit ED-8): the naive form is O(9·r·w·h) — at the slider's max choke of
 * 10 on a 4K frame that is ~1e9 closure-free-but-still-real operations on the MAIN thread, per
 * slider notch, so the Edge-choke control froze the app for seconds per step. A square filter is
 * separable, and each 1D pass uses a monotonic deque, giving O(w·h) TOTAL regardless of radius —
 * amortised ~2 comparisons per pixel per axis.
 */
function morphSquare(
  src: Uint8Array,
  width: number,
  height: number,
  r: number,
  erode: boolean,
): Uint8Array {
  // `better(a, b)` = the value that survives: the smaller one when eroding, else the larger.
  const wins = (a: number, b: number) => (erode ? a <= b : a >= b);
  const span = Math.max(width, height);
  // Deque of INDICES into the current line, kept monotonic so the front is always the extremum
  // of the live window. Allocated once and reused across every row and column.
  const dq = new Int32Array(span);
  const line = new Uint8Array(span);

  const pass = (
    read: (i: number) => number,
    write: (i: number, v: number) => void,
    len: number,
  ) => {
    for (let i = 0; i < len; i++) line[i] = read(i);
    let head = 0, tail = 0; // [head, tail)
    // Prime the window with the first `r` samples so index 0 sees [0, r].
    for (let i = 0; i < Math.min(r, len); i++) {
      while (tail > head && wins(line[i], line[dq[tail - 1]])) tail--;
      dq[tail++] = i;
    }
    for (let i = 0; i < len; i++) {
      const add = i + r;
      if (add < len) {
        while (tail > head && wins(line[add], line[dq[tail - 1]])) tail--;
        dq[tail++] = add;
      }
      const drop = i - r - 1;
      if (drop >= 0 && dq[head] === drop) head++;
      write(i, line[dq[head]]);
    }
  };

  const horiz = new Uint8Array(width * height);
  for (let y = 0; y < height; y++) {
    const row = y * width;
    pass((i) => src[row + i], (i, v) => { horiz[row + i] = v; }, width);
  }
  const out = new Uint8Array(width * height);
  for (let x = 0; x < width; x++) {
    pass((i) => horiz[i * width + x], (i, v) => { out[i * width + x] = v; }, height);
  }
  return out;
}

/**
 * FEATHER — separable box-blur of a single-channel mask plane, `radius` px each
 * axis (window = 2*radius+1). Edge samples clamp to in-bounds neighbours (partial
 * windows average only valid taps). Pure; returns a NEW plane. `radius <= 0` is an
 * identity that returns the SAME `mask` reference (no allocation) — callers must
 * treat it read-only. Extracted from `refineMatte`'s alpha feather; identical math.
 */
export function featherMask(mask: Uint8Array, width: number, height: number, radius: number): Uint8Array {
  const r = Math.max(0, Math.round(radius));
  if (r <= 0) return mask; // identity: SAME reference, do not mutate
  const n = width * height;
  // WS-J / F-38: running-sum box blur. `morphSquare`, directly above, was rewritten to an O(N)
  // monotonic-deque pass for exactly this reason — its own comment says the naive form "froze the
  // app for seconds per step" — and this function was left behind. At the slider's max (r=10,
  // MaskEditor's Edge feather) the naive form is 526.7 ms per call at 2048² and it is called TWICE
  // per compose; this is 90.3 ms.
  //
  // BYTE-IDENTICAL, and the reason must survive any later edit:
  //  - the horizontal pass sums INTEGERS (max 21 × 255 = 5355), so the prefix sum is exact and
  //    `s / c` rounds into Float32Array exactly as the sequential sum did;
  //  - the vertical pass sums Float32 values spanning at most 13 binades (1/21 .. 255) over a
  //    column of ≤ 2048, needing ≤ 47 of a double's 53 mantissa bits — so every partial sum and
  //    every window difference `P[hi+1] - P[lo]` is EXACT, and Math.round agrees byte for byte.
  //    THE PREFIX ARRAY MUST BE Float64Array. A Float32Array prefix loses this.
  //  - the window bounds still CLAMP to in-bounds neighbours and divide by the live tap count,
  //    which is what makes the edges match (an "average over 2r+1 with zero padding" does not).
  const tmp = new Float32Array(n);
  const rowPrefix = new Float64Array(width + 1);
  for (let y = 0; y < height; y++) {
    const row = y * width;
    rowPrefix[0] = 0;
    for (let x = 0; x < width; x++) rowPrefix[x + 1] = rowPrefix[x] + mask[row + x];
    for (let x = 0; x < width; x++) {
      const lo = x - r < 0 ? 0 : x - r;
      const hi = x + r >= width ? width - 1 : x + r;
      const c = hi - lo + 1;
      tmp[row + x] = (rowPrefix[hi + 1] - rowPrefix[lo]) / (c || 1);
    }
  }
  // The vertical pass is a ROLLING window over whole ROWS, not a per-column prefix. Both are O(N);
  // only this one is cache-friendly — a per-column pass strides 8 KB per step at 2048² and measured
  // SLOWER than the naive form it replaced. Add/subtract are exact for the same reason the prefix
  // would have been (every partial sum fits a double's mantissa), so the bytes are unchanged.
  const out = new Uint8Array(n);
  const colSum = new Float64Array(width);
  let lo = 0;
  let hi = r >= height ? height - 1 : r;
  for (let y = lo; y <= hi; y++) {
    const row = y * width;
    for (let x = 0; x < width; x++) colSum[x] += tmp[row + x];
  }
  for (let y = 0; y < height; y++) {
    const wantLo = y - r < 0 ? 0 : y - r;
    const wantHi = y + r >= height ? height - 1 : y + r;
    while (hi < wantHi) {
      hi++;
      const row = hi * width;
      for (let x = 0; x < width; x++) colSum[x] += tmp[row + x];
    }
    while (lo < wantLo) {
      const row = lo * width;
      for (let x = 0; x < width; x++) colSum[x] -= tmp[row + x];
      lo++;
    }
    const c = wantHi - wantLo + 1;
    const row = y * width;
    for (let x = 0; x < width; x++) out[row + x] = Math.round(colSum[x] / (c || 1));
  }
  return out;
}

/**
 * SOFT AI-EDGE COMBINE — replaces the hard per-pixel threshold cut in the AI-edge
 * matte combine (`refined[i] = matte >= protectThresh ? 0 : printSolid`). A soft
 * smoothstep ramp just BELOW the protect threshold removes the salt-and-pepper grain
 * + stair-step boundaries that a soft/noisy BiRefNet matte produces. Two stages:
 *   1. De-speckle: box-blur the matte by `smoothRadius` (default 1) so isolated
 *      speckle pixels average down below the protect band.
 *   2. Smoothstep ramp on [lo, protectThresh] where lo = protectThresh - rampWidth:
 *        protectThresh = 255 - keep*2   (identical to the legacy hard cut)
 *        t = clamp01((ma - lo) / (protectThresh - lo)); s = t*t*(3 - 2*t)
 *        refined = round(printSolid * (1 - s))
 * At ma <= lo → refined = printSolid; at ma >= protectThresh → refined = 0 — so the
 * fully-protected + fully-retained regions match the legacy hard cut exactly.
 * Fixture (mirror in Rust): keep=30 → protectThresh 195, rampWidth 24 → lo 171,
 * midpoint 183 → s=0.5 → refined = 50% printSolid.
 * `rampWidth <= 0` → exact legacy hard cut. `keep=0` → protectThresh 255 (only
 * fully-kept matte pixels protected). `lo` is clamped to >= 0. Counts use the caller's
 * >=128 rule so the ">50% removed → skip" guard stays numerically compatible. Pure.
 */
export function softAiEdgeRefine(
  printSolid: Uint8Array,
  matte: Uint8Array,
  width: number,
  height: number,
  opts: { keep: number; rampWidth?: number; smoothRadius?: number },
): { refined: Uint8Array; baseCount: number; refinedCount: number } {
  const n = width * height;
  const keep = opts.keep;
  const rampWidth = opts.rampWidth ?? 24;
  const smoothRadius = opts.smoothRadius ?? 1;
  const protectThresh = 255 - keep * 2;
  const ma = smoothRadius > 0 ? featherMask(matte, width, height, smoothRadius) : matte;
  const refined = new Uint8Array(n);
  let baseCount = 0, refinedCount = 0;
  const hard = rampWidth <= 0;
  const lo = Math.max(0, protectThresh - rampWidth);
  const denom = protectThresh - lo; // > 0 whenever rampWidth > 0 (protectThresh in [55,255])
  for (let i = 0; i < n; i++) {
    const ps = printSolid[i];
    if (ps >= 128) baseCount++;
    if (hard) {
      refined[i] = ma[i] >= protectThresh ? 0 : ps;
    } else {
      const v = ma[i];
      let s: number;
      if (v <= lo) s = 0;
      else if (v >= protectThresh) s = 1;
      else { const t = (v - lo) / denom; s = t * t * (3 - 2 * t); }
      refined[i] = Math.round(ps * (1 - s));
    }
    if (refined[i] >= 128) refinedCount++;
  }
  return { refined, baseCount, refinedCount };
}

/**
 * MATTE STRENGTH — black-point remap of an alpha plane ("how much fill"). Raises the
 * black point to `bp = round(strength*2)` (0..200) and linearly rescales [bp,255] back
 * to [0,255]; values at/below bp go to 0. `strength <= 0` is an identity that returns
 * the SAME `alpha` reference (no allocation) — callers must treat it read-only.
 *   bp = round(strength*2);  a' = a <= bp ? 0 : round((a - bp) * 255 / (255 - bp))
 * a=255 stays 255 for any strength < 128 (all valid 0..100 inputs). Pure.
 *
 * `out` is an OPTIONAL caller-owned scratch plane reused across calls to avoid a
 * per-call allocation; it is used only on the strength>0 path and only when its length
 * matches `alpha` (otherwise a fresh plane is allocated). `alpha` is never mutated, and
 * the strength<=0 identity still returns the SAME `alpha` reference regardless of `out`.
 * Callers must not pass an `out` that aliases `alpha`.
 */
export function remapMatteStrength(alpha: Uint8Array, strength: number, out?: Uint8Array): Uint8Array {
  if (strength <= 0) return alpha; // identity: SAME reference, do not mutate
  const bp = Math.round(strength * 2);
  const n = alpha.length;
  const dst = out && out.length === n ? out : new Uint8Array(n);
  const range = 255 - bp || 1;
  for (let i = 0; i < n; i++) {
    const a = alpha[i];
    dst[i] = a <= bp ? 0 : Math.round((a - bp) * 255 / range);
  }
  return dst;
}

/** 4-connected flood of `1`-cells reachable from (sx,sy); returns the region mask. */
function floodFrom(grid: Uint8Array, width: number, height: number, sx: number, sy: number): Uint8Array {
  const mask = new Uint8Array(grid.length);
  const start = sy * width + sx;
  if (grid[start] !== 1) return mask;
  const stack = [start];
  mask[start] = 1;
  while (stack.length) {
    const idx = stack.pop()!;
    const x = idx % width;
    const y = (idx - x) / width;
    const trypush = (n: number) => { if (grid[n] === 1 && mask[n] === 0) { mask[n] = 1; stack.push(n); } };
    if (x > 0) trypush(idx - 1);
    if (x < width - 1) trypush(idx + 1);
    if (y > 0) trypush(idx - width);
    if (y < height - 1) trypush(idx + width);
  }
  return mask;
}
